import React, { useEffect, useState } from "react";

import { CheckCircleIcon, ExclamationCircleIcon } from "@heroicons/react/solid";

import { IMarketingGroup } from './../../../models/marketingGroup/MarketingGroup';
import NoItems from "../../../components/Shared/NoItems";
import { useMarketingGroups } from "../../../data/marketingGroup/useMarketingGroups";
import { PageHeader } from "../../../components/Shared/PageHeader";
import LoadingWheel from "../../../components/Shared/LoadingWheel";
import { ButtonInput } from "../../../components/Shared/Inputs/ButtonInput";
import { CreateMarketingGroupFlyout } from "../../../components/MarketingGroups/CreateMarketingGroupFlyout";

interface MarketingGroupRowProps extends IMarketingGroup { }

function MarketingGroupRow({ name, active }: MarketingGroupRowProps) {
  return (
    <tr className="hover:bg-gray-100 hover:cursor-pointer">
      <td className="w-full max-w-0 py-4 pl-4 pr-3 text-sm font-medium text-gray-900 sm:w-auto sm:max-w-none sm:pl-6">
        {!active ?
          <ExclamationCircleIcon
            className="flex-shrink-0 mr-1.5 h-8 w-8 text-red-400"
            aria-hidden="true"
          />
          :
          <CheckCircleIcon
            className="flex-shrink-0 mr-1.5 h-8 w-8 text-green-400"
            aria-hidden="true"
          />
        }
      </td>

      <td className="w-full max-w-0 py-4 pl-4 pr-3 text-sm font-medium text-gray-900 sm:w-auto sm:max-w-none sm:pl-6">
        {name}
      </td>
    </tr>
  );
}

export default function MarketingGroupManager() {
  const { marketingGroups } = useMarketingGroups(true);
  const [createMarketingGroupOpen, setCreateMarketingGroupOpen] = useState(false);

  useEffect(() => {
    document.title = "EstateTrack Admin | Marketing Group Manager";
  }, []);

  const handleCreateClose = () => {
    setCreateMarketingGroupOpen(false);
  }

  return <>
    <CreateMarketingGroupFlyout
      open={createMarketingGroupOpen}
      setOpen={handleCreateClose}
    />
    <div className="px-4 sm:px-6 lg:px-8">
      <PageHeader title="Marketing Group Manager" description="View and manage marketing groups here">
        <ButtonInput
          label="Add Marketing Group"
          isSubmit={undefined}
          onClick={() => setCreateMarketingGroupOpen(true)}
          classes={undefined}
          children={undefined}
        />
      </PageHeader>
      <div className="-mx-4 mt-4 overflow-hidden shadow ring-1 ring-black ring-opacity-5 sm:-mx-6 md:mx-0 md:rounded-lg">
        {marketingGroups.isLoading ? (
          <div className="flex p-10 bg-white justify-center">
            <LoadingWheel width="w-10" height="w-10" />
          </div>
        ) : marketingGroups.data && marketingGroups.data.data.length > 0 ? (
          <table className="min-w-full divide-y divide-gray-300">
            <thead className="bg-gray-50">
              <tr>
                <th
                  scope="col"
                  className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6"
                >
                  Active
                </th>
                <th
                  scope="col"
                  className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6"
                >
                  Name
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 bg-white">
              {marketingGroups.data.data.map((marketingGroup) => (
                <MarketingGroupRow
                  key={marketingGroup.id}
                  {...marketingGroup}
                />
              ))}
            </tbody>
          </table>
        ) : (
          <div className="bg-white p-10">
            {/* @ts-ignore */}
            <NoItems
              itemName="marketing group"
              plural="Marketing Groups"
              onNewClick={() => { }}
            />
          </div>
        )}
      </div>
    </div>
  </>;
}